import React, { useContext, useEffect, useRef, useState } from "react";
import { ApiError } from "../../Models/ApiError";
import { toast } from "react-toastify";
import { dataContext } from "../../Context/contextProvider";
import { observer } from "mobx-react";
import { ValidationResult } from "../../Models/ValidationResult";
import { useOrder } from "../../Context/useOrder";
import { LoginToDatabaseRequest } from "../../Models/LoginToDatabaseRequest";
import { useAuth } from "../../Context/useAuth";
import { UserInfo } from "../../Models/UserInfo";
import { WarehouseLocation } from "../../Models/WarehouseLocation";

interface DbLoginValidationErrors {
  dbUsername?: ValidationResult;
  dbPassword?: ValidationResult;
  dbKey?: ValidationResult;
  location?: ValidationResult;
}

interface Step3Props {
  onNavigateToMenu?: () => void;
  onNavigateToLogin?: () => void;
  dbKey: string;
}

// login to database
const Step3 = observer(({ onNavigateToMenu, onNavigateToLogin, dbKey }: Step3Props) => {
  const { setCurrentOrder } = useOrder();
  const { getDeviceId, loginToDatabase } = useAuth();
  const dataStore = useContext(dataContext);
  const [submitDisabled, setSubmitDisabled] = useState(false);

  //the username last signed in with on this device, for this server and database, is filled in for next time
  const lastUsernameKey = `e_lastusername_${process.env.REACT_APP_PROVIDER}_${dataStore.serverUrl || ""}_${dbKey || ""}`;
  const [localDbUsername, setLocalDbUsername] = useState<string>(() => {
    try {
      return localStorage.getItem(lastUsernameKey) || "";
    } catch {
      return "";
    }
  });
  const [localDbPassword, setLocalDbPassword] = useState<string>("");
  const [errors, setErrors] = useState<DbLoginValidationErrors>({});
  //set when the user has more than one warehouse location to pick from
  const [warehouseLocations, setWarehouseLocations] = useState<WarehouseLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!dataStore.serverUrl) {
      return onNavigateToLogin?.();
    }
  }, [dataStore.serverUrl, onNavigateToLogin]);
  const blurAll = () => {
    if (ref1.current) {
      ref1.current.blur();
    }
    if (ref2.current) {
      ref2.current.blur();
    }
  };
  const handleLogin = async () => {
    //check form validity
    const err: DbLoginValidationErrors = {};
    let hasErrors: boolean = false;
    if (!localDbUsername) {
      err.dbUsername = {
        message: "Username is required",
        valid: false,
      };
      hasErrors = true;
    }
    setErrors(err);
    if (hasErrors) {
      return;
    }
    blurAll();
    setSubmitDisabled(true);
    dataStore.dbKey = null;
    dataStore.location = null;
    setWarehouseLocations([]);
    setSelectedLocation("");
    setCurrentOrder(null);
    try {
      const model: LoginToDatabaseRequest = {
        serverUrl: dataStore.serverUrl || "",
        dbKey: dbKey || "",
        username: localDbUsername,
        password: localDbPassword || "",
        deviceId: getDeviceId() || "",
      };
      const userInfo: UserInfo | null = await loginToDatabase(model);
      if (userInfo && userInfo?.dbPermission?.dbKey === dbKey) {
        try {
          localStorage.setItem(lastUsernameKey, localDbUsername);
        } catch {
          //storage blocked - they just type it next time
        }
        const locations: WarehouseLocation[] = userInfo.warehouseLocations || [];
        if (locations.length > 1) {
          //user has to pick a location before going on
          setWarehouseLocations(locations);
        } else {
          if (locations.length === 1) {
            dataStore.location = locations[0].locationNo;
          }
          dataStore.dbKey = dbKey;
          onNavigateToMenu?.();
        }
      } else {
        toast.error("Wrong username or password");
      }
      setSubmitDisabled(false);
    } catch (err) {
      const error = err as ApiError;
      setSubmitDisabled(false);
      if (error?.code?.toUpperCase() !== "ERR_NETWORK") {
        toast.error("Wrong Company Username or Password");
      }
    }
  };
  const handleContinue = () => {
    const location = warehouseLocations.find((l) => String(l.locationNo) === selectedLocation);
    if (!location) {
      setErrors({
        location: {
          message: "Please select a location",
          valid: false,
        },
      });
      return;
    }
    setErrors({});
    dataStore.location = location.locationNo;
    dataStore.dbKey = dbKey;
    onNavigateToMenu?.();
  };
  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter") {
      if (warehouseLocations.length > 1) {
        handleContinue();
      } else {
        handleLogin();
      }
    }
  };
  const makePasswordInvisible = (e: React.PointerEvent<HTMLElement>): void => {
    const element = document.getElementById("step3_password_toggle");
    if (element) {
      element.className = "fa fa-eye-slash";
    }
    if (ref2.current) {
      ref2.current.type = "password";
    }
    e.stopPropagation();
  };
  const makePasswordVisible = (e: React.PointerEvent<HTMLElement>): void => {
    const element = document.getElementById("step3_password_toggle");
    if (element) {
      element.className = "fa fa-eye";
    }
    if (ref2.current) {
      ref2.current.type = "text";
    }
    e.stopPropagation();
  };
  return (
    <>
      <div className="row row-border-top pt-2 mb-2">
        <div className="col-lg-4 offset-lg-4 col-md-6 offset-md-3 col-sm-8 offset-sm-2 col-12 mb-2">
          {/* a real login form (fixed username / current-password fields) so the browser's own password manager
              offers to save the login and fills it in next time. Submitting is still done by handleLogin. */}
          <form className="d-grid gap-2" onKeyDown={handleFormKeyDown} onSubmit={(e) => e.preventDefault()} autoComplete="on">
            <div>
              <label htmlFor="dbUsername">Company Username</label>
              <div className="d-flex">
                <input
                  ref={ref1}
                  name="username"
                  id="dbUsername"
                  type="text"
                  className={errors?.dbUsername ? "form-control is-invalid" : "form-control"}
                  autoComplete="username"
                  autoCapitalize="none"
                  value={localDbUsername}
                  onChange={(e) => {
                    setLocalDbUsername(e.target.value);
                    setWarehouseLocations([]);
                  }}
                />
              </div>
              {errors?.dbUsername && (
                <div className="invalid-feedback d-block">
                  <div>{errors.dbUsername?.message}</div>
                </div>
              )}
            </div>
            <div>
              <label htmlFor="dbPassword">Company Password</label>
              <div className="d-flex">
                <div className="input-group">
                  <input
                    ref={ref2}
                    name="password"
                    id="dbPassword"
                    type="password"
                    className={errors?.dbPassword ? "form-control is-invalid" : "form-control"}
                    autoComplete="current-password"
                    value={localDbPassword}
                    onChange={(e) => {
                      setLocalDbPassword(e.target.value);
                      setWarehouseLocations([]);
                    }}
                  />
                  <span className="input-group-text" onPointerDown={makePasswordVisible} onPointerUp={makePasswordInvisible}>
                    <i id="step3_password_toggle" className="fa fa-eye-slash" style={{ cursor: "pointer" }}></i>
                  </span>
                </div>
                <div>
                  <button disabled={submitDisabled} className="btn btn-primary ms-2" style={{ width: "50px" }} type="button" onClick={handleLogin}>
                    <i className="fa fa-solid fa-arrow-right" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
              {errors?.dbPassword && (
                <div className="invalid-feedback d-block">
                  <div>{errors.dbPassword?.message}</div>
                </div>
              )}
            </div>
            {warehouseLocations.length > 1 && (
              <div>
                <label htmlFor="step3_location">Location</label>
                <select
                  id="step3_location"
                  className={errors?.location ? "form-select is-invalid" : "form-select"}
                  value={selectedLocation}
                  onChange={(e) => {
                    setSelectedLocation(e.target.value);
                    setErrors({});
                  }}
                >
                  <option value="">Select a location...</option>
                  {warehouseLocations.map((l) => (
                    <option key={l.locationNo} value={String(l.locationNo)}>
                      {l.code}
                    </option>
                  ))}
                </select>
                {errors?.location && (
                  <div className="invalid-feedback d-block">
                    <div>{errors.location?.message}</div>
                  </div>
                )}
                <div className="d-grid mt-2">
                  <button className="btn btn-primary" type="button" onClick={handleContinue}>
                    Continue
                  </button>
                </div>
              </div>
            )}
            <div className="d-grid">{submitDisabled && <p className="text-center">Please wait...</p>}</div>
          </form>
        </div>
      </div>
    </>
  );
});

export default Step3;
